/**
 * Trace: follow one keyword or url through the fetch stack.
 *
 * A dry run shows what would happen now, and why, with nothing fetched. A real run
 * queues the fetch (and, for a keyword, its top result pages) and shows each step
 * as the fetch worker writes it: queued, which adapter and why not the others,
 * which proxy, what the site showed, what was remembered, what was written and
 * how it was read (which results parser or site parser, structured data, text).
 * Backend: jcseo/fetching/trace.py and explain.py.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import type {
  TraceMeta,
  TracePageText,
  TraceReadNote,
  TraceRequestState,
  TraceStep,
} from "../types";

const POLL_MS = 2000;
// A real run is followed this long at most; it can be reopened from the list.
const POLL_FOR_MS = 15 * 60 * 1000;
const FINISHED = new Set(["consumed", "failed", "cancelled"]);

const DOT: Record<TraceStep["level"], string> = {
  ok: "bg-green-500",
  info: "bg-gray-400",
  warn: "bg-amber-500",
  bad: "bg-red-500",
};

// The details a step may carry, in the order and words they are shown.
const DETAILS: [string, string][] = [
  ["skipped", "Not used"],
  ["notes", "Note"],
  ["proxy", "Proxy"],
  ["passed_over", "Proxies passed over"],
  ["proxies", "Proxies"],
  ["status_code", "Status"],
  ["seconds", "Seconds"],
  ["tools", "Bot protection seen"],
  ["remembered", "Remembered"],
  ["read", "How it was read"],
  ["blocks", "Blocks on record"],
  ["now", "Now"],
  ["browsers", "Browsers"],
  ["device", "Device"],
  ["house", "Shared browser"],
];

const input =
  "px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100";
const button =
  "px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50";

function isReadNote(v: unknown): v is TraceReadNote {
  return typeof v === "object" && v !== null && "page" in v && "show" in v;
}

function Detail({
  value,
  onOpen,
}: {
  value: unknown;
  onOpen: (note: TraceReadNote) => void;
}) {
  if (Array.isArray(value)) {
    return (
      <ul className="list-disc pl-4">
        {value.map((v, i) => (
          <li key={i}>
            {isReadNote(v) ? (
              <button
                type="button"
                onClick={() => onOpen(v)}
                className="text-left text-blue-600 dark:text-blue-400 hover:underline"
              >
                {v.text}
              </button>
            ) : (
              String(v)
            )}
          </li>
        ))}
      </ul>
    );
  }
  return <span>{value === true ? "yes" : String(value)}</span>;
}

const SHOW_TITLES: Record<TraceReadNote["show"], string> = {
  content: "The page's content, as kept from the site",
  text: "The plain text read from the page",
  parsed: "What the site parser read",
};

/** A traced page's text, as stored now (GET /network/trace/page/...). */
function PageText({
  note,
  onClose,
}: {
  note: TraceReadNote;
  onClose: () => void;
}) {
  const [got, setGot] = useState<TracePageText | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    api
      .getTracePage(note.page, note.show)
      .then((r) => live && setGot(r))
      .catch(
        (e) =>
          live &&
          setError(e instanceof Error ? e.message : "Could not read the page"),
      );
    return () => {
      live = false;
    };
  }, [note]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-500/75" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-xl">
        <div className="flex items-start justify-between gap-4 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {SHOW_TITLES[note.show]}
            </h3>
            {got && (
              <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                {got.url} · {got.chars.toLocaleString()} characters
                {got.cut && " (cut short)"}
                {got.last_crawled &&
                  ` · stored ${new Date(got.last_crawled).toLocaleString()}`}
                . A later fetch of this page replaces it.
              </p>
            )}
          </div>
          <button type="button" onClick={onClose} className={button}>
            Close
          </button>
        </div>
        <div className="overflow-auto p-4">
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          {!got && !error && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
          )}
          {got && !got.body && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nothing stored.
            </p>
          )}
          {got?.body && (
            <pre className="text-xs whitespace-pre-wrap break-words text-gray-800 dark:text-gray-200">
              {got.body}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function StepList({
  steps,
  startedAt,
}: {
  steps: TraceStep[];
  startedAt: number;
}) {
  const [open, setOpen] = useState<TraceReadNote | null>(null);
  // Grouped by request, in the order each first appears: a keyword's search, then
  // each result page.
  const groups = useMemo(() => {
    const out: { key: string; label: string; steps: TraceStep[] }[] = [];
    const at: Record<string, number> = {};
    for (const s of steps) {
      const key = s.request_id != null ? `#${s.request_id}` : (s.request ?? "");
      if (at[key] === undefined) {
        at[key] = out.length;
        out.push({
          key,
          label: s.request
            ? `${s.request}${s.request_id != null ? ` (#${s.request_id})` : ""}`
            : "",
          steps: [],
        });
      }
      out[at[key]].steps.push(s);
    }
    return out;
  }, [steps]);

  return (
    <div className="space-y-4">
      {open && <PageText note={open} onClose={() => setOpen(null)} />}
      {groups.map((g) => (
        <div key={g.key || "general"}>
          {g.label && (
            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1 break-all">
              {g.label}
            </h4>
          )}
          <ol className="space-y-2 border-l border-gray-200 dark:border-gray-700 ml-1">
            {g.steps.map((s, i) => (
              <li key={i} className="pl-3 relative">
                <span
                  className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${DOT[s.level]}`}
                />
                <div className="text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-xs text-gray-400 mr-2 tabular-nums">
                    +{Math.max(0, s.at - startedAt).toFixed(1)} s
                  </span>
                  {s.text}
                </div>
                {s.data && (
                  <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 gap-y-0.5 text-xs text-gray-600 dark:text-gray-300">
                    {DETAILS.filter(([k]) => s.data?.[k] !== undefined).map(
                      ([k, label]) => (
                        <div key={k} className="contents">
                          <dt className="text-gray-400">{label}</dt>
                          <dd className="break-all">
                            <Detail value={s.data?.[k]} onOpen={setOpen} />
                          </dd>
                        </div>
                      ),
                    )}
                  </dl>
                )}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function Requests({ rows }: { rows: TraceRequestState[] }) {
  if (rows.length === 0) return null;
  return (
    <table className="min-w-full mb-4">
      <thead>
        <tr>
          {["Request", "Status", "With", "Error"].map((h) => (
            <th
              key={h}
              className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 pb-1 pr-4"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="text-xs text-gray-700 dark:text-gray-200">
            <td className="py-1 pr-4 break-all">
              #{r.id} {r.what}
            </td>
            <td className="py-1 pr-4">{r.status}</td>
            <td className="py-1 pr-4">{r.adapter ?? ""}</td>
            <td className="py-1 pr-4 text-red-600 dark:text-red-400">
              {r.error ?? ""}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function NetworkTrace() {
  const [searchParams] = useSearchParams();
  const [kind, setKind] = useState<"keyword" | "url">(
    searchParams.get("url") ? "url" : "keyword",
  );
  const [subject, setSubject] = useState(searchParams.get("url") ?? "");
  const [top, setTop] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [waiting, setWaiting] = useState<{
    queued: string[];
    unsearched: string[];
  }>({ queued: [], unsearched: [] });
  const [recent, setRecent] = useState<TraceMeta[]>([]);
  // What is shown: a dry run's steps, or a real run being followed.
  const [dry, setDry] = useState<{ steps: TraceStep[]; at: number } | null>(
    null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<{
    meta: TraceMeta;
    steps: TraceStep[];
    requests: TraceRequestState[];
  } | null>(null);
  const followUntil = useRef(0);

  useEffect(() => {
    api
      .getTraceWaiting()
      .then(setWaiting)
      .catch(() => undefined);
    api
      .getTraces()
      .then((r) => setRecent(r.traces))
      .catch(() => undefined);
  }, []);

  const poll = useCallback(async (id: string) => {
    try {
      const got = await api.getTrace(id);
      setRun(got);
      return got;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not read the trace");
      return null;
    }
  }, []);

  useEffect(() => {
    if (!runId) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      const got = await poll(runId);
      const done =
        got != null &&
        got.requests.length > 0 &&
        got.requests.every((r) => FINISHED.has(r.status)) &&
        // A search queues its pages as it is written: give them a moment to appear.
        Date.now() / 1000 - (got.steps[got.steps.length - 1]?.at ?? 0) > 10;
      if (!stop && !done && Date.now() < followUntil.current)
        timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [runId, poll]);

  const start = async (mode: "dry" | "real") => {
    const text = subject.trim();
    if (!text) return;
    if (
      mode === "real" &&
      !window.confirm(
        "This sends real fetches through the queue, as you: they use proxies and browsers, can be blocked, and are remembered like any other fetch. Go on?",
      )
    )
      return;
    setBusy(true);
    setError("");
    try {
      const got = await api.startTrace({ kind, subject: text, mode, top });
      if (got.mode === "dry") {
        setRunId(null);
        setRun(null);
        setDry({ steps: got.steps, at: got.steps[0]?.at ?? Date.now() / 1000 });
      } else {
        setDry(null);
        setRun(null);
        followUntil.current = Date.now() + POLL_FOR_MS;
        setRunId(got.trace_id);
        api
          .getTraces()
          .then((r) => setRecent(r.traces))
          .catch(() => undefined);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start the trace");
    } finally {
      setBusy(false);
    }
  };

  const open = (id: string) => {
    setDry(null);
    setRun(null);
    followUntil.current = Date.now() + POLL_FOR_MS;
    setRunId(id);
  };

  const pick = (value: string) => {
    setKind("keyword");
    setSubject(value);
  };

  const startedAt = run ? new Date(run.meta.started_at).getTime() / 1000 : 0;
  const following =
    runId && run && !run.requests.every((r) => FINISHED.has(r.status));

  return (
    <div className="space-y-6" data-testid="network-trace">
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3">
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as "keyword" | "url")}
            className={input}
          >
            <option value="keyword">Keyword</option>
            <option value="url">Web page</option>
          </select>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && start("dry")}
            placeholder={
              kind === "keyword"
                ? "best crm for startups"
                : "https://www.g2.com/…"
            }
            className={`${input} flex-1 min-w-[12rem]`}
          />
          {kind === "keyword" && (
            <label className="text-xs text-gray-600 dark:text-gray-300 flex items-center gap-1">
              Result pages
              <input
                type="number"
                min={1}
                max={10}
                value={top}
                onChange={(e) =>
                  setTop(
                    Math.max(1, Math.min(10, parseInt(e.target.value) || 1)),
                  )
                }
                className={`${input} w-16`}
              />
            </label>
          )}
          <button
            type="button"
            disabled={busy || !subject.trim()}
            onClick={() => start("dry")}
            className={button}
          >
            Dry run
          </button>
          <button
            type="button"
            disabled={busy || !subject.trim()}
            onClick={() => start("real")}
            className={`${button} border-blue-500 text-blue-700 dark:text-blue-300`}
          >
            Run for real
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          A dry run fetches nothing and shows what would happen now. A real run
          fetches it (and for a keyword, its top result pages) through the whole
          stack, and shows each step as it happens.
        </p>
        {(waiting.queued.length > 0 || waiting.unsearched.length > 0) && (
          <div className="text-xs space-y-1">
            {waiting.queued.length > 0 && (
              <div>
                <span className="text-gray-500 dark:text-gray-400 mr-2">
                  Waiting in the queue:
                </span>
                {waiting.queued.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => pick(q)}
                    className="mr-2 mb-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            {waiting.unsearched.length > 0 && (
              <div>
                <span className="text-gray-500 dark:text-gray-400 mr-2">
                  Not searched yet:
                </span>
                {waiting.unsearched.map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => pick(q)}
                    className="mr-2 mb-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
      </section>

      {dry && (
        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Dry run
          </h3>
          <StepList steps={dry.steps} startedAt={dry.at} />
        </section>
      )}

      {runId && (
        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-x-auto">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {run
              ? `${run.meta.kind === "keyword" ? "Keyword" : "Page"}: ${run.meta.subject}`
              : "Starting…"}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {following
              ? "Following: new steps appear every couple of seconds."
              : "Finished."}
          </p>
          {run && <Requests rows={run.requests} />}
          {run && <StepList steps={run.steps} startedAt={startedAt} />}
        </section>
      )}

      {recent.length > 0 && (
        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Recent real runs (kept a day)
          </h3>
          <ul className="text-xs space-y-1">
            {recent.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  onClick={() => open(t.id)}
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {t.kind === "keyword" ? "Keyword" : "Page"}: {t.subject}
                </button>
                <span className="text-gray-400">
                  {" "}
                  · {new Date(t.started_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
